import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from "@mui/material";
import {
  People as PeopleIcon,
  LocalCarWash as CarWashIcon,
  CheckCircle as CheckCircleIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  RateReview as ReviewIcon,
  DirectionsCar as DirectionsCarIcon,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import statisticsService, { IStatistics } from "../../../services/statistics.service";
import { logger } from "../../../utils/logger";
import { useRole } from "../../../context/RoleContext";
import RMDashboard from "./RMDashboard";

const Home = () => {
  const { isRelationshipManager } = useRole();

  // RM gets a dedicated, scoped dashboard; super_admin sees the full overview below.
  // Render the RM branch as its own component so this component's hooks (which fetch
  // the global super_admin statistics) are isolated to the super_admin path.
  if (isRelationshipManager) return <RMDashboard />;

  return <SuperAdminHome />;
};

// Shared hover/focus affordance for stat cards that navigate somewhere. Kept as a
// plain sx object (not a styled Card) so every card below stays a normal MUI <Card>
// and only opts in by spreading this + an onClick/tabIndex/role/onKeyDown quartet.
const navigableCardSx = {
  height: "100%",
  cursor: "pointer",
  transition: "box-shadow 0.15s ease, transform 0.15s ease",
  "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
  "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 2 },
};

const SuperAdminHome = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [stats, setStats] = useState<IStatistics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Cards below only navigate where a real destination page exists (verified against
  // src/index.tsx's route tree). Washers has no dedicated global list (washers are
  // scoped per-owner under manage-owners/:ownerID/employees), and Revenue/Pending
  // Reviews/Quick Stats Summary have no top-level route — those four stay
  // non-interactive rather than link to a route that doesn't exist.
  const goToOwners = () => navigate("/dashboard/manage-owners");
  const goToCarWashes = () => navigate("/dashboard/manage-car-washes");
  const goToUsers = () => navigate("/dashboard/manage-users");
  const goToWashes = () => navigate("/dashboard/washes");

  // Enter/Space activation for keyboard users on a div-based "button" (the Card
  // itself isn't a native interactive element even with role="button").
  const onActivateKey =
    (handler: () => void) => (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    };

  useEffect(() => {
    fetchStatistics();
    // Mount-only fetch; fetchStatistics is a plain (non-memoized) function
    // recreated every render, so adding it here would refetch on every
    // render instead of once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await statisticsService.getDashboardStats();
      setStats(data);
      logger.info("Fetched dashboard statistics");
    } catch (err: any) {
      setError(err.message || t("dashboard.failedToFetch"));
      logger.error("Failed to fetch statistics", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Box className="p-4">
        <Alert severity="error" onClose={() => setError(null)}>
          {error || t("dashboard.failedToLoad")}
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="p-4">
      <Typography variant="h4" gutterBottom fontWeight="bold">
        {t("dashboard.title")}
      </Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom mb={3}>
        {t("dashboard.welcome")}
      </Typography>

      <Grid container spacing={3}>
        {/* Owners Stats */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card
            sx={navigableCardSx}
            onClick={goToOwners}
            role="button"
            tabIndex={0}
            onKeyDown={onActivateKey(goToOwners)}
            aria-label={t("dashboard.owners")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  {t("dashboard.owners")}
                </Typography>
                <PeopleIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.owners.total}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.activeCount", { count: stats.owners.active })}
                  color="success"
                  size="small"
                />
                <Chip
                  label={t("dashboard.pendingCount", { count: stats.owners.pendingApproval })}
                  color="warning"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Car Washes Stats */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card
            sx={navigableCardSx}
            onClick={goToCarWashes}
            role="button"
            tabIndex={0}
            onKeyDown={onActivateKey(goToCarWashes)}
            aria-label={t("dashboard.carWashes")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  {t("dashboard.carWashes")}
                </Typography>
                <CarWashIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.carWashes.total}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.activeCount", { count: stats.carWashes.active })}
                  color="success"
                  size="small"
                />
                <Chip
                  label={t("dashboard.pendingCount", { count: stats.carWashes.pendingReview })}
                  color="warning"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Users Stats */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card
            sx={navigableCardSx}
            onClick={goToUsers}
            role="button"
            tabIndex={0}
            onKeyDown={onActivateKey(goToUsers)}
            aria-label={t("dashboard.users")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  {t("dashboard.users")}
                </Typography>
                <DirectionsCarIcon color="secondary" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.users.total}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.activeCount", { count: stats.users.active })}
                  color="success"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Washers Stats */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  {t("dashboard.washers")}
                </Typography>
                <PeopleIcon color="action" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" gutterBottom>
                {stats.washers.total}
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.activeCount", { count: stats.washers.active })}
                  color="success"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Wash Requests Stats */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card
            sx={navigableCardSx}
            onClick={goToWashes}
            role="button"
            tabIndex={0}
            onKeyDown={onActivateKey(goToWashes)}
            aria-label={t("dashboard.washRequests")}
          >
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{t("dashboard.washRequests")}</Typography>
                <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.total")}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold">
                    {stats.washes.total}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.completed")}
                  </Typography>
                  <Typography variant="h4" fontWeight="bold" color="success.main">
                    {stats.washes.completed}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.pending")}
                  </Typography>
                  <Typography variant="h5" color="warning.main">
                    {stats.washes.pending}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.inProgress")}
                  </Typography>
                  <Typography variant="h5" color="primary.main">
                    {stats.washes.inProgress}
                  </Typography>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.todayCount", { count: stats.washes.completedToday })}
                  color="success"
                  size="small"
                />
                <Chip
                  label={t("dashboard.thisWeekCount", { count: stats.washes.completedThisWeek })}
                  color="info"
                  size="small"
                />
                <Chip
                  label={t("dashboard.thisMonthCount", { count: stats.washes.completedThisMonth })}
                  color="primary"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Revenue Stats */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">{t("dashboard.revenue")}</Typography>
                <MoneyIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="h3" fontWeight="bold" color="success.main" gutterBottom>
                {stats.revenue.totalCommission.toFixed(2)} ₼
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {t("dashboard.totalCommission")}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.today")}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.revenue.commissionToday.toFixed(2)} ₼
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.thisWeek")}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.revenue.commissionThisWeek.toFixed(2)} ₼
                  </Typography>
                </Grid>
                <Grid size={{ xs: 4 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t("dashboard.thisMonth")}
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {stats.revenue.commissionThisMonth.toFixed(2)} ₼
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Reviews Stats */}
        <Grid size={{ xs: 12, md: 6, lg: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" color="text.secondary">
                  {t("dashboard.pendingReviews")}
                </Typography>
                <ReviewIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" fontWeight="bold" color="warning.main" gutterBottom>
                {stats.reviews.pending}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("dashboard.requireAttention")}
              </Typography>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip
                  label={t("dashboard.approvedTodayCount", { count: stats.reviews.approvedToday })}
                  color="success"
                  size="small"
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Quick Stats Summary */}
        <Grid size={{ xs: 12, md: 6, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t("dashboard.quickOverview")}
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2}>
                    <TrendingUpIcon color="success" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {stats.carWashes.active}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("dashboard.activeCarWashes")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2}>
                    <PendingIcon color="warning" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {stats.washes.pending}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("dashboard.pendingWashes")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2}>
                    <CheckCircleIcon color="success" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {stats.washes.completedToday}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("dashboard.completedToday")}
                    </Typography>
                  </Box>
                </Grid>
                <Grid size={{ xs: 6, sm: 3 }}>
                  <Box textAlign="center" p={2}>
                    <ReviewIcon color="warning" sx={{ fontSize: 32 }} />
                    <Typography variant="h5" fontWeight="bold">
                      {stats.reviews.pending}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("dashboard.pendingReviews")}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Home;
